import * as React from 'react';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@/utils';

const ToastProvider = React.forwardRef(({ isExistOpenToasts, ...props }, ref) => (
	<div
		ref={ref}
		className="fixed inset-x-0 bottom-20 sm:bottom-10 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:flex-col pointer-events-none"
		{...props}
	/>
));
ToastProvider.displayName = 'ToastProvider';

const ToastViewport = React.forwardRef(({ ...props }, ref) => (
	<div
		ref={ref}
		className="fixed bottom-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:right-0 sm:flex-col md:max-w-[420px]"
		{...props}
	/>
));
ToastViewport.displayName = 'ToastViewport';

const toastVariants = cva(
	'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
	{
		variants: {
			variant: {
				default: 'border bg-black text-foreground md:max-w-[420px] sm:ml-auto',
				destructive:
					'destructive group border-destructive bg-destructive text-destructive-foreground md:max-w-[420px] sm:ml-auto',
				success:
					'success group border-green-600 bg-green-600 text-white md:max-w-[420px] sm:ml-auto',
				'toast.probability.all-markets-traded':
					'border-none bg-[#14AE5C] text-white shadow-[0_16px_32px_rgba(12,12,13,0.1),0_4px_4px_rgba(12,12,13,0.05)] !rounded-[8px] !py-[8px] !px-[16px] !space-x-0 gap-[14px] sm:mx-auto sm:max-w-[600px]',
				'toast.friend-request':
					'border-none bg-[#1C2D4A] text-white shadow-[0_16px_32px_rgba(12,12,13,0.1),0_4px_4px_rgba(12,12,13,0.05)] !rounded-[8px] !py-[8px] !px-[16px] !space-x-0 gap-[14px] sm:mx-auto sm:max-w-[361px]',
				'admin.snackbar':
					'border-none bg-[#2d3a6c] text-white shadow-[0_1px_2px_rgba(12,12,13,0.1),0_1px_2px_rgba(12,12,13,0.05)] !rounded-[8px] !py-2 !px-3 !space-x-0 gap-[14px] md:max-w-[420px] sm:ml-auto',
				'admin.snackbar.error':
					'border-none bg-[#2d3a6c] text-[#da3030] shadow-[0_1px_2px_rgba(12,12,13,0.1),0_1px_2px_rgba(12,12,13,0.05)] !rounded-[8px] !py-2 !px-3 !space-x-0 gap-[14px] md:max-w-[420px] sm:ml-auto',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	}
);

const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
	return <div ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />;
});
Toast.displayName = 'Toast';

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn(
			'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive group-[.success]:border-muted/40 group-[.success]:hover:border-green-500/30 group-[.success]:hover:bg-green-700 group-[.success]:hover:text-white group-[.success]:focus:ring-green-400',
			className
		)}
		{...props}
	/>
));
ToastAction.displayName = 'ToastAction';

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
	<button
		ref={ref}
		className={cn(
			'absolute right-2 top-2 rounded-md p-1 text-foreground/50 hover:text-foreground focus:outline-none focus:ring-2 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600 group-[.success]:text-green-100 group-[.success]:hover:text-white group-[.success]:focus:ring-green-400 group-[.success]:focus:ring-offset-green-600 opacity-100',
			className
		)}
		toast-close=""
		{...props}
	>
		<X className="h-4 w-4" />
	</button>
));
ToastClose.displayName = 'ToastClose';

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
	<div ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = 'ToastTitle';

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
	<div ref={ref} className={cn('text-sm opacity-90', className)} {...props} />
));
ToastDescription.displayName = 'ToastDescription';

export {
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
	ToastAction,
};
